import { useNavigate } from 'react-router-dom';
import { createCase, updateCase, addDocumentToCase, addNoteToCase, addContactToCase, getDocumentDownloadUrl } from '../services/api';
import { useModals } from './useModals';
import { useCases } from './useCases';
import axios from 'axios';

interface UseHomeHandlersProps {
  cases: ReturnType<typeof useCases>;
  modals: ReturnType<typeof useModals>;
  logout: () => void;
}

/**
 * Custom hook that provides all event handlers for the Home page
 * Coordinates between the various state management hooks and API calls
 */
export const useHomeHandlers = ({ cases, modals, logout }: UseHomeHandlersProps) => {
  const navigate = useNavigate();

  // Profile menu handlers
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Case management handlers
  const handleCreateCase = async () => {
    if (!modals.newCaseData.projectName || !modals.newCaseData.clientName) {
      alert('Please fill in Project Name and Client Name');
      return;
    }

    modals.setIsCreatingCase(true);
    try {
      await createCase(modals.newCaseData);
      modals.resetNewCaseModal();
      await cases.loadCases(1);
    } catch (error) {
      alert('Failed to create case. Please try again.');
    } finally {
      modals.setIsCreatingCase(false);
    }
  };

  const handleEditCase = () => {
    if (!cases.caseDetail) return;
    modals.setEditData({
      projectName: cases.caseDetail.projectName,
      clientName: cases.caseDetail.clientName,
      status: cases.caseDetail.status,
      phase: cases.caseDetail.phase,
      description: cases.caseDetail.description || '',
    });
    modals.setIsEditModalOpen(true);
  };

  const handleUpdateCase = async () => {
    if (!cases.selectedCaseId) return;

    modals.setIsUpdating(true);
    try {
      await updateCase(cases.selectedCaseId, modals.editData);
      modals.setIsEditModalOpen(false);
      await cases.refreshCaseDetail();
      await cases.loadCases(cases.currentPage);
    } catch (error) {
      alert('Failed to update case. Please try again.');
    } finally {
      modals.setIsUpdating(false);
    }
  };

  // Document handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      modals.setSelectedDocFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!modals.selectedDocFile || !cases.selectedCaseId) return;

    modals.setIsUploadingDoc(true);
    modals.setUploadDocProgress('Preparing upload...');

    try {
      modals.setUploadDocProgress('Getting upload URL...');
      const response = await addDocumentToCase(cases.selectedCaseId, {
        fileName: modals.selectedDocFile.name,
        fileType: modals.selectedDocFile.type,
        fileSize: modals.selectedDocFile.size,
      });

      modals.setUploadDocProgress('Uploading file...');
      await axios.put(response.uploadUrl, modals.selectedDocFile, {
        headers: {
          'Content-Type': modals.selectedDocFile.type,
        },
      });

      modals.setUploadDocProgress('Upload complete!');
      modals.resetUploadModal();
      await cases.refreshCaseDetail();
      await cases.refreshTimeline();
    } catch (error) {
      alert('Failed to upload document. Please try again.');
    } finally {
      modals.setIsUploadingDoc(false);
      modals.setUploadDocProgress('');
    }
  };

  const handleDownloadDocument = async (docId: string) => {
    if (!cases.selectedCaseId) return;
    try {
      const response = await getDocumentDownloadUrl(cases.selectedCaseId, docId, false);
      window.open(response.downloadUrl, '_blank');
    } catch (error) {
      alert('Failed to download document. Please try again.');
    }
  };

  const handlePreviewDocument = async (docId: string) => {
    if (!cases.selectedCaseId) return;
    try {
      const response = await getDocumentDownloadUrl(cases.selectedCaseId, docId, true);
      modals.setPreviewDocument({
        fileName: response.fileName,
        fileType: response.fileType,
        downloadUrl: response.downloadUrl,
        docId: docId,
      });
    } catch (error) {
      alert('Failed to preview document. Please try again.');
    }
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!modals.noteData.body || !cases.selectedCaseId) return;

    modals.setIsAddingNote(true);
    try {
      await addNoteToCase(cases.selectedCaseId, modals.noteData);
      modals.resetNoteModal();
      await cases.refreshCaseDetail();
      await cases.refreshTimeline();
    } catch (error) {
      alert('Failed to add note. Please try again.');
    } finally {
      modals.setIsAddingNote(false);
    }
  };

  // Contact handlers
  const handleAddContact = async () => {
    if (!modals.contactData.name || !modals.contactData.role || !cases.selectedCaseId) return;

    modals.setIsAddingContact(true);
    try {
      await addContactToCase(cases.selectedCaseId, modals.contactData);
      modals.resetContactModal();
      await cases.refreshCaseDetail();
      await cases.refreshTimeline();
    } catch (error) {
      alert('Failed to add contact. Please try again.');
    } finally {
      modals.setIsAddingContact(false);
    }
  };

  return {
    // Profile handlers
    handleLogout,
    
    // Case handlers
    handleCreateCase,
    handleEditCase,
    handleUpdateCase,
    
    // Document handlers
    handleFileSelect,
    handleUploadDocument,
    handleDownloadDocument,
    handlePreviewDocument,
    
    // Note handlers
    handleAddNote,
    
    // Contact handlers
    handleAddContact,
  };
};

